
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Subscription } from "@/types/subscription";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { PaymentSummary } from "./PaymentSummary";
import { SubscriptionOptions } from "./SubscriptionOptions";
import { SubscriptionFormFields } from "./SubscriptionFormFields";
import { PromoCodeField } from "./form-fields/PromoCodeField";
import { useSubscriptionFormSubmission } from "./SubscriptionFormSubmission";
import { useSubscriptionFormPayment } from "./SubscriptionFormPayment";

interface SubscriptionFormContainerProps {
  onSubmit: (data: Omit<Subscription, "id" | "totalPaid">) => Promise<Subscription>;
  isLoading: boolean;
}

export function SubscriptionFormContainer({ onSubmit, isLoading }: SubscriptionFormContainerProps) {
  const [createdSubscription, setCreatedSubscription] = useState<Subscription | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [promoDiscount, setPromoDiscount] = useState<number>(0);
  const [finalAmount, setFinalAmount] = useState<number>(5.00);
  
  const form = useForm({
    defaultValues: {
      customerName: "",
      email: "",
      address: "",
      houseNumber: "",
      street: "",
      townCity: "",
      postcode: "",
      binDay: "", // Will be set automatically by the bot
      isAnnual: false,
      isBusiness: false,
      businessName: "",
      multipleDays: false,
      promoCode: "", // Add promo code field
    },
    mode: "onChange"
  });

  const { handleSubmit } = useSubscriptionFormSubmission({
    form,
    onSubmit,
    onSubscriptionCreated: setCreatedSubscription,
    onPaymentError: setPaymentError
  });

  const { isProcessingPayment, handlePayment } = useSubscriptionFormPayment({
    subscription: createdSubscription,
    onPaymentError: setPaymentError,
    discountAmount: promoDiscount
  });

  const handleCreateNew = () => {
    setCreatedSubscription(null);
    setPaymentError(null);
    setPromoDiscount(0);
    setFinalAmount(5.00);
    form.reset();
  };

  const handlePromoCodeApplied = (discountAmount: number, newFinalAmount: number) => {
    setPromoDiscount(discountAmount);
    setFinalAmount(newFinalAmount);
  };

  return (
    <>
      {createdSubscription ? (
        <PaymentSummary
          subscription={createdSubscription}
          paymentError={paymentError}
          isProcessingPayment={isProcessingPayment}
          onPayment={handlePayment}
          onCreateNew={handleCreateNew}
          discountAmount={promoDiscount}
        />
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <SubscriptionFormFields form={form} />
            <SubscriptionOptions form={form} />
            {/* PromoCodeField temporarily disabled */}
            {/* <PromoCodeField 
              form={form} 
              onPromoCodeApplied={handlePromoCodeApplied}
              originalAmount={5.00}
            /> */}
            
            {/* Price Summary */}
            {promoDiscount > 0 && (
              <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Original price:</span>
                    <span>£5.00</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-700">
                    <span>Discount:</span>
                    <span>-£{promoDiscount.toFixed(2)}</span>
                  </div>
                  <hr className="border-green-200" />
                  <div className="flex justify-between font-medium text-green-800">
                    <span>Final price:</span>
                    <span>£{finalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end">
              <Button type="submit" disabled={isLoading || !form.formState.isValid}>
                {isLoading ? "Creating..." : "Create Subscription"}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </>
  );
}
